/**
 * Canonical production origin. All social/canonical URLs resolve here
 * so that LinkedIn / Facebook / X scrapers always point at the .com.
 */
export const SITE_ORIGIN = "https://www.juanthetrailerguy.com";
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/images/jtg-logo-preview.png`;

export interface PageMetaOptions {
  /** Absolute or root-relative image URL for og:image / twitter:image. */
  image?: string;
  /** og:type — "website" | "article" | "product" | "profile" ... */
  type?: string;
  /** twitter:card — defaults to summary_large_image. */
  twitterCard?: "summary" | "summary_large_image";
  /** Optional alt text for the social image. */
  imageAlt?: string;
  /** Optional distinct og:title / twitter:title (defaults to the page title). */
  ogTitle?: string;
  /** Optional distinct og:description / twitter:description. */
  ogDescription?: string;
}

const toAbsolute = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url)) return url;
  return `${SITE_ORIGIN}${url.startsWith("/") ? "" : "/"}${url}`;
};

const upsertMeta = (
  attrName: "name" | "property",
  key: string,
  value: string,
) => {
  const selector = `meta[${attrName}="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};

export const setPageMeta = (
  title: string,
  description?: string,
  canonical?: string,
  opts: PageMetaOptions = {},
) => {
  if (typeof document === "undefined") return;

  document.title = title;

  // Canonical — always absolute on the .com origin.
  const path =
    canonical ?? (typeof window !== "undefined" ? window.location.pathname : "/");
  const absoluteUrl = /^https?:\/\//i.test(path)
    ? path
    : `${SITE_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;

  let canonicalEl = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (!canonicalEl) {
    canonicalEl = document.createElement("link");
    canonicalEl.setAttribute("rel", "canonical");
    document.head.appendChild(canonicalEl);
  }
  canonicalEl.setAttribute("href", absoluteUrl);

  // Open Graph / Twitter — always emit url, type, image, card so the
  // homepage values don't leak onto other routes.
  const ogType = opts.type ?? "website";
  const ogImage = toAbsolute(opts.image) ?? DEFAULT_OG_IMAGE;
  const twitterCard = opts.twitterCard ?? "summary_large_image";

  upsertMeta("property", "og:title", opts.ogTitle ?? title);
  upsertMeta("name", "twitter:title", opts.ogTitle ?? title);
  upsertMeta("property", "og:url", absoluteUrl);
  upsertMeta("property", "og:type", ogType);
  upsertMeta("property", "og:image", ogImage);
  upsertMeta("property", "og:site_name", "Juan The Trailer Guy");
  upsertMeta("property", "og:locale", "en_US");
  upsertMeta("name", "twitter:image", ogImage);
  upsertMeta("name", "twitter:card", twitterCard);
  upsertMeta("name", "twitter:site", "@juanthetrailerguy");
  if (opts.imageAlt) {
    upsertMeta("property", "og:image:alt", opts.imageAlt);
    upsertMeta("name", "twitter:image:alt", opts.imageAlt);
  }

  if (description) {
    upsertMeta("name", "description", description);
    upsertMeta("property", "og:description", opts.ogDescription ?? description);
    upsertMeta("name", "twitter:description", opts.ogDescription ?? description);
  }
};
export interface HreflangPair {
  /** e.g. "en-US", "es-US", "x-default" */
  lang: string;
  /** Root-relative or absolute URL */
  href: string;
}

/**
 * Upserts <link rel="alternate" hreflang="..."> tags for a set of
 * language/URL pairs. Call once per page (English and Spanish versions
 * both call this with the same pair list) and clean up on unmount by
 * invoking the returned function.
 */
export const setHreflang = (pairs: HreflangPair[]): (() => void) => {
  if (typeof document === "undefined") return () => {};

  const created: HTMLLinkElement[] = [];

  pairs.forEach(({ lang, href }) => {
    const absoluteHref = /^https?:\/\//i.test(href)
      ? href
      : `${SITE_ORIGIN}${href.startsWith("/") ? "" : "/"}${href}`;
    const el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", lang);
    el.setAttribute("href", absoluteHref);
    document.head.appendChild(el);
    created.push(el);
  });

  return () => {
    created.forEach((el) => el.remove());
  };
};
